import { captureEvent, useCaptureEvent } from "@Light/app/analytics";
import { QueryLoader } from "@Light/components/app/QueryLoader";
import { Button } from "@Light/components/page/Button";
import { PageBody } from "@Light/components/page/PageBody";
import { useLight } from "@Light/services/light";
import type { Utility } from "@Light/services/lightTypes";
import { zip5 } from "@Light/utils/address";
import { Lightning01 } from "@untitledui/icons";
import { useState } from "react";
import { utilityAndPostalCodeToRequirementsPatch } from "../address/patch";
import { ServiceLocationSheets } from "../address/ServiceLocationSheets";
import { useServiceLocationSheets } from "../address/useServiceLocationSheets";
import { EnrollHelpButtonFooter } from "../EnrollHelpButtonFooter";
import { useEnrollment } from "../flow/context";
import { PartnerTosDisclaimer } from "../PartnerTosDisclaimer";
import { useBrokerTermsAgreement } from "../useBrokerTermsAgreement";
import { UtilityRadioGroup } from "./UtilityRadioGroup";

export const ZipUtilitySplit = () => {
  const { requirements, next } = useEnrollment();
  const { useCheckEligibilityQuery } = useLight();

  const existing = requirements.service_address?.value?.postal_code;
  const postalCode = existing ? zip5(existing) : undefined;
  const query = useCheckEligibilityQuery(
    { postal_code: postalCode ?? "" },
    { skip: !postalCode },
  );

  const utilities = query.data?.utilities;
  useCaptureEvent({
    event: "enroll_zip_split_viewed",
    condition: true,
    properties: {
      variant: "screen",
      postal_code: postalCode ?? null,
      utility_count: utilities?.length ?? 0,
    },
  });

  const brokerTermsAgreement = useBrokerTermsAgreement();
  const sheets = useServiceLocationSheets({
    postalCode,
    additionalRequirements: brokerTermsAgreement,
  });

  return (
    <QueryLoader
      queries={[query]}
      errorMessage="Error checking which utilities serve your area. Please try again later"
    >
      <BaseZipUtilitySplit
        postalCode={postalCode}
        utilities={utilities ?? []}
        onSubmit={(utility) => {
          if (!postalCode) return;
          captureEvent("enroll_zip_split_submitted", {
            variant: "screen",
            postal_code: postalCode,
            utility: utility.id,
          });
          void next({
            ...utilityAndPostalCodeToRequirementsPatch(
              requirements,
              postalCode,
              utility.id,
            ),
            ...brokerTermsAgreement,
          });
        }}
        onClickProvideAddress={() => {
          sheets.openSheet("address");
        }}
      />
      <ServiceLocationSheets {...sheets} />
    </QueryLoader>
  );
};

export type BaseZipUtilitySplitProps = {
  postalCode?: string;
  utilities: Utility[];
  onSubmit: (utility: Utility) => void;
  onClickProvideAddress: () => void;
};

export const BaseZipUtilitySplit = ({
  postalCode,
  utilities,
  onSubmit,
  onClickProvideAddress,
}: BaseZipUtilitySplitProps) => {
  const [selected, setSelected] = useState<Utility | undefined>();

  return (
    <PageBody
      centered
      icon={Lightning01}
      title="Let’s find your electricity plan. Who is your local utility?"
      subtitle={`${postalCode ? `Zip code ${postalCode}` : "Your zip code"} is split between utility providers. We need your local utility for accurate pricing.`}
      footer={
        <div className="flex flex-col gap-3">
          <EnrollHelpButtonFooter>
            <Button
              size="lg"
              className="w-full"
              disabled={!selected}
              onClick={() => {
                if (selected) onSubmit(selected);
              }}
              data-testid="zip-utility-split-submit"
            >
              View offer
            </Button>
          </EnrollHelpButtonFooter>
          <PartnerTosDisclaimer className="text-center" />
        </div>
      }
    >
      <UtilityRadioGroup
        name="zip-utility-split"
        utilities={utilities}
        value={selected}
        onChange={setSelected}
        onClickProvideAddress={onClickProvideAddress}
      />
    </PageBody>
  );
};
